import { useState, useEffect, useCallback } from 'react';
import { SchoolSelector } from '@/components/SchoolSelector';
import { incidentsApi, usersApi, schoolsApi } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { cn, utcDate } from '@/lib/utils';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import {
  FileText, UserCheck, Search, XCircle, CheckCircle,
  ChevronRight, Clock, AlertTriangle, ArrowLeft, Filter,
  MessageSquare, User, Shield
} from 'lucide-react';

const statusConfig = {
  open: { label: 'Nytt', color: 'bg-red-100 text-red-800 border-red-200', icon: AlertTriangle },
  assigned: { label: 'Tilldelat', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: UserCheck },
  investigating: { label: 'Utreds', color: 'bg-amber-100 text-amber-800 border-amber-200', icon: Search },
  action_taken: { label: 'Åtgärd vidtagen', color: 'bg-indigo-100 text-indigo-800 border-indigo-200', icon: Shield },
  closed: { label: 'Stängt', color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle },
};

const priorityConfig = {
  high: { label: 'Hög', color: 'bg-red-600 text-white' },
  medium: { label: 'Medel', color: 'bg-orange-500 text-white' },
  low: { label: 'Låg', color: 'bg-slate-400 text-white' },
};

const outcomeLabels = {
  vape_confirmed: 'Vape bekräftad',
  spray: 'Spray (ej vape)',
  nothing_found: 'Inget hittades',
  inconclusive: 'Oklart resultat',
  other: 'Annat',
};

const timelineActionLabels = { created: 'Skapat', assigned: 'Tilldelat', updated: 'Uppdaterat', closed: 'Stängt' };
const eventTypeLabels = { vape: 'Vape', suspect: 'Misstänkt', offline: 'Offline', test: 'Testlarm' };

const timelineActionIcons = {
  created: Clock,
  assigned: UserCheck,
  closed: CheckCircle,
};

function StatusBadge({ status }) {
  const cfg = statusConfig[status] || statusConfig.open;
  return (
    <span className={cn("text-xs font-semibold uppercase px-2 py-0.5 rounded border inline-flex items-center gap-1", cfg.color)} data-testid={`incident-status-${status}`}>
      {cfg.label}
    </span>
  );
}

function PriorityBadge({ priority }) {
  const cfg = priorityConfig[priority] || priorityConfig.medium;
  return (
    <span className={cn("text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded", cfg.color)} data-testid={`incident-priority-${priority}`}>
      {cfg.label}
    </span>
  );
}

// ===== Incident Detail View =====
function IncidentDetail({ incident, users, onBack, onRefresh }) {
  const { user } = useAuth();
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [assignTo, setAssignTo] = useState('');
  const [assignNote, setAssignNote] = useState('');
  const [updateNote, setUpdateNote] = useState('');
  const [updateStatus, setUpdateStatus] = useState('');
  const [closeOutcome, setCloseOutcome] = useState('');
  const [closeNote, setCloseNote] = useState('');
  const [followUp, setFollowUp] = useState(false);
  const [followUpDate, setFollowUpDate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleAssign = async () => {
    if (!assignTo) return;
    setSubmitting(true);
    try {
      await incidentsApi.assign(incident.id, { assigned_to: assignTo, note: assignNote || null });
      toast.success('Incident tilldelad');
      setAssignDialogOpen(false);
      setAssignTo('');
      setAssignNote('');
      onRefresh();
    } catch { toast.error('Kunde inte tilldela'); }
    finally { setSubmitting(false); }
  };

  const handleUpdate = async () => {
    if (!updateNote) return;
    setSubmitting(true);
    try {
      await incidentsApi.update(incident.id, { status: updateStatus || null, note: updateNote });
      toast.success('Incident uppdaterad');
      setUpdateDialogOpen(false);
      setUpdateNote('');
      setUpdateStatus('');
      onRefresh();
    } catch { toast.error('Kunde inte uppdatera'); }
    finally { setSubmitting(false); }
  };

  const handleClose = async () => {
    if (!closeOutcome) return;
    setSubmitting(true);
    try {
      await incidentsApi.close(incident.id, {
        outcome: closeOutcome,
        note: closeNote || null,
        follow_up_required: followUp,
        follow_up_date: followUpDate || null,
      });
      toast.success('Ärende stängt');
      setCloseDialogOpen(false);
      onRefresh();
    } catch { toast.error('Kunde inte stänga'); }
    finally { setSubmitting(false); }
  };

  const isClosed = incident.status === 'closed';

  return (
    <div className="space-y-6" data-testid="incident-detail">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="incident-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Tillbaka
        </Button>
      </div>

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h2 className="font-chivo font-black text-2xl text-navy dark:text-white" data-testid="incident-case-number">
              {incident.case_number}
            </h2>
            <StatusBadge status={incident.status} />
            <PriorityBadge priority={incident.priority} />
          </div>
          <p className="text-sm text-slate-500 font-ibm">
            {incident.school_name} &mdash; {incident.room_name || 'Okänt rum'}
          </p>
          <p className="text-sm text-slate-500 font-ibm mt-1">
            Skapad: {format(utcDate(incident.created_at), 'd MMM yyyy HH:mm', { locale: sv })}
          </p>
          {incident.assigned_to_name && (
            <p className="text-sm text-slate-600 dark:text-slate-300 font-ibm mt-1">
              <User className="h-3.5 w-3.5 inline mr-1" />
              Tilldelad: <span className="font-semibold">{incident.assigned_to_name}</span>
            </p>
          )}
        </div>

        {!isClosed && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setAssignDialogOpen(true)} data-testid="assign-incident-btn">
              <UserCheck className="h-4 w-4 mr-1" /> Tilldela
            </Button>
            <Button size="sm" variant="outline" onClick={() => setUpdateDialogOpen(true)} data-testid="update-incident-btn">
              <MessageSquare className="h-4 w-4 mr-1" /> Uppdatera
            </Button>
            <Button size="sm" variant="default" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setCloseDialogOpen(true)} data-testid="close-incident-btn">
              <CheckCircle className="h-4 w-4 mr-1" /> Stäng ärende
            </Button>
          </div>
        )}
      </div>

      {/* Event Info Card */}
      <Card className="border-slate-200 dark:border-slate-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-ibm font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide">Larminfo</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <span className="text-slate-500 dark:text-slate-400">Typ</span>
              <p className="font-semibold text-slate-900 dark:text-white capitalize">{eventTypeLabels[incident.event_type] || incident.event_type || '-'}</p>
            </div>
            <div>
              <span className="text-slate-500 dark:text-slate-400">Allvarlighetsgrad</span>
              <p className="font-semibold text-slate-900 dark:text-white capitalize">{priorityConfig[incident.severity]?.label || incident.severity || '-'}</p>
            </div>
            <div>
              <span className="text-slate-500 dark:text-slate-400">Konfidens</span>
              <p className="font-semibold text-slate-900 dark:text-white">{incident.score ? `${Math.round(incident.score * 100)}%` : '-'}</p>
            </div>
            <div>
              <span className="text-slate-500 dark:text-slate-400">Utfall</span>
              <p className="font-semibold text-slate-900 dark:text-white">{incident.outcome ? outcomeLabels[incident.outcome] || incident.outcome : 'Öppet'}</p>
            </div>
          </div>
          {incident.follow_up_required && (
            <div className="mt-2 px-3 py-2 rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 text-xs font-ibm">
              Uppföljning krävs{incident.follow_up_date ? ` — ${incident.follow_up_date}` : ''}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Timeline */}
      <Card className="border-slate-200 dark:border-slate-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-ibm font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide">Tidslinje</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative" data-testid="incident-timeline">
            {(incident.timeline || []).map((entry, idx) => {
              const IconComp = timelineActionIcons[entry.action] || MessageSquare;
              const isLast = idx === (incident.timeline || []).length - 1;
              return (
                <div key={idx} className="flex gap-4 pb-6 last:pb-0 relative">
                  {!isLast && <div className="absolute left-[15px] top-8 bottom-0 w-px bg-slate-200 dark:bg-slate-700" />}
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center shrink-0 z-10",
                    entry.action === 'created' ? 'bg-slate-100 dark:bg-slate-700 text-slate-500' :
                    entry.action === 'closed' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600' :
                    'bg-blue-100 dark:bg-blue-900/30 text-blue-600'
                  )}>
                    <IconComp className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-semibold text-slate-900 dark:text-white font-ibm">{timelineActionLabels[entry.action] || entry.action}</span>
                      <span className="text-slate-400 dark:text-slate-500 text-xs">
                        {entry.at ? format(utcDate(entry.at), 'd MMM HH:mm', { locale: sv }) : ''}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-300 mt-0.5">{entry.note?.replace(/^Incident skapad/, 'Ärende skapat').replace('suspect-larm', 'misstänkt-larm')}</p>
                    {entry.by_name && (
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{entry.by_name}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-chivo">Tilldela ärende</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-ibm font-medium text-slate-700 dark:text-slate-300 block mb-1">Välj person</label>
              <Select value={assignTo} onValueChange={setAssignTo}>
                <SelectTrigger data-testid="assign-select">
                  <SelectValue placeholder="Välj person..." />
                </SelectTrigger>
                <SelectContent>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.email}>{u.name} ({u.role})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-ibm font-medium text-slate-700 dark:text-slate-300 block mb-1">Anteckning (valfritt)</label>
              <Textarea value={assignNote} onChange={e => setAssignNote(e.target.value)} placeholder="Lägg till kommentar..." data-testid="assign-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAssignDialogOpen(false)}>Avbryt</Button>
            <Button onClick={handleAssign} disabled={!assignTo || submitting} data-testid="assign-confirm-btn">Tilldela</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Dialog */}
      <Dialog open={updateDialogOpen} onOpenChange={setUpdateDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-chivo">Uppdatera ärende</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-ibm font-medium text-slate-700 dark:text-slate-300 block mb-1">Ny status</label>
              <Select value={updateStatus} onValueChange={setUpdateStatus}>
                <SelectTrigger data-testid="update-status-select">
                  <SelectValue placeholder="Behåll nuvarande..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="investigating">Utreds</SelectItem>
                  <SelectItem value="action_taken">Åtgärd vidtagen</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-ibm font-medium text-slate-700 dark:text-slate-300 block mb-1">Anteckning</label>
              <Textarea value={updateNote} onChange={e => setUpdateNote(e.target.value)} placeholder="Beskriv vad som gjorts..." data-testid="update-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setUpdateDialogOpen(false)}>Avbryt</Button>
            <Button onClick={handleUpdate} disabled={!updateNote || submitting} data-testid="update-confirm-btn">Uppdatera</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Dialog */}
      <Dialog open={closeDialogOpen} onOpenChange={setCloseDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-chivo">Stäng ärende</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-ibm font-medium text-slate-700 dark:text-slate-300 block mb-1">Utfall</label>
              <Select value={closeOutcome} onValueChange={setCloseOutcome}>
                <SelectTrigger data-testid="close-outcome-select">
                  <SelectValue placeholder="Välj utfall..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vape_confirmed">Vape bekräftad</SelectItem>
                  <SelectItem value="spray">Spray (ej vape)</SelectItem>
                  <SelectItem value="nothing_found">Inget hittades</SelectItem>
                  <SelectItem value="inconclusive">Oklart resultat</SelectItem>
                  <SelectItem value="other">Annat</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-ibm font-medium text-slate-700 dark:text-slate-300 block mb-1">Anteckning (valfritt)</label>
              <Textarea value={closeNote} onChange={e => setCloseNote(e.target.value)} placeholder="Sammanfattning..." data-testid="close-note" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="follow-up" checked={followUp} onChange={e => setFollowUp(e.target.checked)} className="rounded" data-testid="follow-up-checkbox" />
              <label htmlFor="follow-up" className="text-sm font-ibm text-slate-700 dark:text-slate-300">Uppföljning krävs</label>
            </div>
            {followUp && (
              <div>
                <label className="text-sm font-ibm font-medium text-slate-700 dark:text-slate-300 block mb-1">Uppföljningsdatum</label>
                <Input type="date" value={followUpDate} onChange={e => setFollowUpDate(e.target.value)} data-testid="follow-up-date" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCloseDialogOpen(false)}>Avbryt</Button>
            <Button onClick={handleClose} disabled={!closeOutcome || submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="close-confirm-btn">Stäng ärende</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===== Main Incidents Page =====
export default function IncidentsPage() {
  const { isSuperAdmin } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [users, setUsers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [schoolFilter, setSchoolFilter] = useState('all');
  const [selectedIncident, setSelectedIncident] = useState(null);

  const loadIncidents = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (schoolFilter !== 'all') params.school_id = schoolFilter;
      const res = await incidentsApi.list(params);
      let data = res.data;
      if (priorityFilter !== 'all') {
        data = data.filter(i => i.priority === priorityFilter);
      }
      setIncidents(data);
    } catch {
      toast.error('Kunde inte ladda ärenden');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter, schoolFilter]);

  useEffect(() => {
    loadIncidents();
  }, [loadIncidents]);

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [usersRes, schoolsRes] = await Promise.all([usersApi.list(), schoolsApi.list()]);
        setUsers(usersRes.data);
        setSchools(schoolsRes.data);
      } catch {}
    };
    loadMeta();
  }, []);

  const handleSelectIncident = async (id) => {
    try {
      const res = await incidentsApi.get(id);
      setSelectedIncident(res.data);
    } catch {
      toast.error('Kunde inte ladda ärende');
    }
  };

  const handleRefresh = async () => {
    if (selectedIncident) {
      try {
        const res = await incidentsApi.get(selectedIncident.id);
        setSelectedIncident(res.data);
      } catch {}
    }
    loadIncidents();
  };

  if (loading) {
    return (
      <div className="p-6 md:p-10 flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-slate-500 font-ibm">Laddar ärenden...</div>
      </div>
    );
  }

  if (selectedIncident) {
    return (
      <div className="p-6 md:p-10" data-testid="incidents-page">
        <IncidentDetail
          incident={selectedIncident}
          users={users}
          onBack={() => setSelectedIncident(null)}
          onRefresh={handleRefresh}
        />
      </div>
    );
  }

  // Count stats
  const openCount = incidents.filter(i => i.status !== 'closed').length;
  const highCount = incidents.filter(i => i.priority === 'high' && i.status !== 'closed').length;

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="incidents-page">
      <div>
        <h1 className="font-chivo font-black text-2xl md:text-3xl text-navy dark:text-white tracking-tight">
          Ärendehantering
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1 font-ibm">
          Spåra och följ upp varje larm från detektion till avslut
        </p>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-200 dark:border-slate-700">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-chivo font-black text-navy dark:text-white">{incidents.length}</p>
            <p className="text-xs text-slate-500 font-ibm uppercase tracking-wide">Totalt</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-chivo font-black text-red-700 dark:text-red-400">{openCount}</p>
            <p className="text-xs text-slate-500 font-ibm uppercase tracking-wide">Öppna</p>
          </CardContent>
        </Card>
        <Card className="border-orange-200 dark:border-orange-800 bg-orange-50/50 dark:bg-orange-900/10">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-chivo font-black text-orange-700 dark:text-orange-400">{highCount}</p>
            <p className="text-xs text-slate-500 font-ibm uppercase tracking-wide">Hög prioritet</p>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-900/10">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-chivo font-black text-emerald-700 dark:text-emerald-400">{incidents.filter(i => i.status === 'closed').length}</p>
            <p className="text-xs text-slate-500 font-ibm uppercase tracking-wide">Stängda</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px] bg-white dark:bg-slate-800" data-testid="incident-status-filter">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla statusar</SelectItem>
            <SelectItem value="open">Nytt</SelectItem>
            <SelectItem value="assigned">Tilldelat</SelectItem>
            <SelectItem value="investigating">Utreds</SelectItem>
            <SelectItem value="action_taken">Åtgärd vidtagen</SelectItem>
            <SelectItem value="closed">Stängt</SelectItem>
          </SelectContent>
        </Select>

        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-[160px] bg-white dark:bg-slate-800" data-testid="incident-priority-filter">
            <SelectValue placeholder="Prioritet" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla prioriteter</SelectItem>
            <SelectItem value="high">Hög</SelectItem>
            <SelectItem value="medium">Medel</SelectItem>
            <SelectItem value="low">Låg</SelectItem>
          </SelectContent>
        </Select>

        <SchoolSelector schools={schools} value={schoolFilter} onChange={setSchoolFilter} triggerClassName="w-[180px] bg-white dark:bg-slate-800" />
      </div>

      {/* Incident List */}
      <div className="space-y-3">
        {incidents.map(inc => (
          <Card
            key={inc.id}
            className={cn(
              "border cursor-pointer hover:shadow-md transition-shadow",
              inc.status === 'closed' ? 'border-slate-200 dark:border-slate-700 opacity-70' :
              inc.priority === 'high' ? 'border-red-200 dark:border-red-800 bg-red-50/30 dark:bg-red-900/5' :
              'border-slate-200 dark:border-slate-700'
            )}
            onClick={() => handleSelectIncident(inc.id)}
            data-testid={`incident-card-${inc.id}`}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1.5">
                    <span className="font-chivo font-bold text-navy dark:text-white text-sm" data-testid={`incident-case-${inc.case_number}`}>
                      {inc.case_number}
                    </span>
                    <StatusBadge status={inc.status} />
                    <PriorityBadge priority={inc.priority} />
                    <span className={cn(
                      "text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded",
                      inc.event_type === 'vape' ? 'bg-red-200 text-red-800' : 'bg-amber-200 text-amber-800'
                    )}>
                      {inc.event_type === 'vape' ? 'VAPE' : 'MISSTÄNKT'}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-300 font-ibm">
                    {inc.school_name} &mdash; {inc.room_name || 'Okänt rum'}
                  </p>
                  <div className="flex items-center gap-4 mt-1 text-xs text-slate-400 dark:text-slate-500">
                    <span>{format(utcDate(inc.created_at), 'd MMM yyyy HH:mm', { locale: sv })}</span>
                    {inc.assigned_to_name && (
                      <span className="inline-flex items-center gap-1">
                        <User className="h-3 w-3" /> {inc.assigned_to_name}
                      </span>
                    )}
                    {inc.outcome && (
                      <span>{outcomeLabels[inc.outcome] || inc.outcome}</span>
                    )}
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-slate-400 dark:text-slate-600 shrink-0" />
              </div>
            </CardContent>
          </Card>
        ))}

        {incidents.length === 0 && (
          <Card className="border-slate-200 dark:border-slate-700">
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
              <h3 className="font-chivo text-lg text-slate-700 dark:text-slate-300">Inga ärenden hittades</h3>
              <p className="text-slate-500 dark:text-slate-400 mt-1 font-ibm text-sm">
                Ärenden skapas automatiskt när nya vape- eller misstänkt-larm detekteras
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
